import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Table, Button, Badge, Row, Col, Form, Spinner, Alert, Pagination } from 'react-bootstrap';
import axios from 'axios';

const AdminSubscriptionsPage = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [plans, setPlans] = useState([]);
  const [savingKey, setSavingKey] = useState(null);
  const [success, setSuccess] = useState(null);

  const profileLabels = {
    crm: 'Somente CRM',
    clinic_full: 'Clínica Full sem CRM',
    clinic_full_crm: 'Clínica Full com CRM (sem Campanhas)',
    clinic_full_crm_campaigns: 'Clínica Full com CRM + Campanhas',
  };

  const fetchSubscriptions = useCallback(async (page = 1) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get('/api/admin/subscriptions', {
        params: {
          page,
          status: filterStatus,
        },
      });
      setSubscriptions(response.data.data || []);
      setPagination({
        currentPage: response.data.current_page,
        totalPages: response.data.last_page,
      });
    } catch (err) {
      setError('Não foi possível carregar a lista de assinaturas.');
      console.error("Erro ao buscar assinaturas:", err);
    } finally {
      setIsLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    fetchSubscriptions(1);
  }, [fetchSubscriptions]);

  useEffect(() => {
    axios.get('/api/admin/plans/module-profiles')
      .then(response => setPlans(response.data || []))
      .catch(() => setError('Não foi possível carregar a configuração dos planos.'));
  }, []);

  const updatePlanProfile = async (plan, moduleProfile) => {
    setSavingKey('plan-' + plan.id); setError(null); setSuccess(null);
    try {
      const response = await axios.put('/api/admin/plans/' + plan.id + '/module-profile', { module_profile: moduleProfile });
      setPlans(current => current.map(item => item.id === plan.id ? response.data.plan : item));
      setSuccess('Perfil do plano atualizado e aplicado às assinaturas vinculadas.');
      await fetchSubscriptions(pagination?.currentPage || 1);
    } catch (err) { setError(err.response?.data?.message || 'Não foi possível atualizar o perfil do plano.'); }
    finally { setSavingKey(null); }
  };

  const updateOdontologia = async (subscription, enabled) => {
    setSavingKey('subscription-' + subscription.id); setError(null); setSuccess(null);
    try {
      const response = await axios.put('/api/admin/subscriptions/' + subscription.id + '/modules', { odontologia_incluida: enabled });
      setSubscriptions(current => current.map(item => item.id === subscription.id ? { ...item, ...response.data.subscription } : item));
      setSuccess('Disponibilidade do módulo Odontologia atualizada.');
    } catch (err) { setError(err.response?.data?.message || 'Não foi possível atualizar Odontologia.'); }
    finally { setSavingKey(null); }
  };
  const getStatusInfo = (status) => {
    switch (status) {
      case 'active': return { variant: 'success', text: 'Ativa' };
      case 'past_due': return { variant: 'warning', text: 'Pendente' };
      case 'canceled': return { variant: 'danger', text: 'Cancelada' };
      case 'trialing': return { variant: 'info', text: 'Trial' };
      case 'unpaid': return { variant: 'danger', text: 'Não Paga' };
      default: return { variant: 'secondary', text: status };
    }
  };

  return (
    <Container fluid>
      <h1 className="mb-4">Gerenciamento de Assinaturas</h1>
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}
      <Card className="mb-4">
        <Card.Header>Perfis de módulos dos planos</Card.Header>
        <Card.Body>
          <Alert variant="info">O perfil é aplicado automaticamente às assinaturas vinculadas. Planos sem perfil preservam os módulos atuais.</Alert>
          {plans.length === 0 && <Alert variant="warning">Nenhum plano cadastrado. Cadastre ou sincronize os planos antes de definir seus perfis de módulos.</Alert>}
          <Row>{plans.map(plan => (
            <Col lg={4} md={6} key={plan.id} className="mb-3"><Form.Group>
              <Form.Label><strong>{plan.name}</strong></Form.Label>
              <Form.Select value={plan.module_profile || ''} disabled={savingKey === 'plan-' + plan.id} onChange={event => updatePlanProfile(plan, event.target.value)}>
                <option value="" disabled>Selecione o perfil</option>
                {Object.entries(profileLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </Form.Select>
            </Form.Group></Col>
          ))}</Row>
        </Card.Body>
      </Card>
      <Card className="mb-4">
        <Card.Header>Filtros</Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group controlId="filterStatus">
                <Form.Label>Filtrar por Status</Form.Label>
                <Form.Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                  <option value="">Todos os Status</option>
                  <option value="active">Ativa</option>
                  <option value="past_due">Pendente</option>
                  <option value="canceled">Cancelada</option>
                  <option value="trialing">Trial</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>Lista de Assinaturas</Card.Header>
        <Card.Body>
          {isLoading ? (
            <div className="text-center"><Spinner animation="border" /></div>
          ) : error ? (
            <Alert variant="danger">{error}</Alert>
          ) : (
            <>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Clínica</th>
                    <th>Plano</th>
                    <th className="text-end">Valor (R$)</th>
                    <th className="text-center">Status</th>
                    <th>Próximo Vencimento</th>
                    <th className="text-center">Odontologia</th>
                    <th className="text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.map(sub => {
                    const statusInfo = getStatusInfo(sub.status);
                    return (
                      <tr key={sub.id}>
                        <td>{sub.entidade?.nome || 'N/A'}</td>
                        <td>{sub.plan?.name || 'N/A'}</td>
                        <td className="text-end">{parseFloat(sub.plan?.price || 0).toFixed(2)}</td>
                        <td className="text-center"><Badge bg={statusInfo.variant}>{statusInfo.text}</Badge></td>
                        <td>{sub.current_period_ends_at ? new Date(sub.current_period_ends_at).toLocaleDateString('pt-BR') : 'N/A'}</td>
                        <td className="text-center">
                          <Form.Check type="switch" checked={Boolean(sub.odontologia_incluida)}
                            disabled={!sub.plan?.module_profile || sub.plan.module_profile === 'crm' || savingKey === 'subscription-' + sub.id}
                            onChange={event => updateOdontologia(sub, event.target.checked)}
                            aria-label={'Odontologia para ' + (sub.entidade?.nome || 'empresa')} />
                          {sub.plan?.module_profile === 'crm' && <small className="text-muted">Não aplicável</small>}
                        </td>
                        <td className="text-center"><Button variant="outline-info" size="sm">Ver Histórico</Button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              {pagination && pagination.totalPages > 1 && (
                <Pagination className="justify-content-center">
                  <Pagination.Prev onClick={() => fetchSubscriptions(pagination.currentPage - 1)} disabled={pagination.currentPage === 1} />
                  {[...Array(pagination.totalPages).keys()].map(number => (
                    <Pagination.Item key={number + 1} active={number + 1 === pagination.currentPage} onClick={() => fetchSubscriptions(number + 1)}>
                      {number + 1}
                    </Pagination.Item>
                  ))}
                  <Pagination.Next onClick={() => fetchSubscriptions(pagination.currentPage + 1)} disabled={pagination.currentPage === pagination.totalPages} />
                </Pagination>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default AdminSubscriptionsPage;